import { Injectable } from "@angular/core";
import {
  OrderAttributes,
  OrderDetailAttributes,
} from "@albatrosdeveloper/ave-models-package";
import { CombinedFilter } from "@albatrosdeveloper/ave-utils-npm/lib/utils/query.util";
import { ExportStoreService } from "./export-overlay.store";
import {
  ExportFileFormat,
  ExportRange,
  ExportTaskStatus,
} from "./tasks/core/export-task.enum";
import { Observable } from "rxjs";
import { OrderCustomerExport } from "./tasks/exports/order-customer/order-customer.export";
import { OrderCustomerService } from "app/modules/components/reports/historic/order-customer/order-customer.service";
import { OrdersListService } from "app/modules/components/sales/order-list-components/order-list.service";
import { OpentItemsService } from "app/modules/components/reports/historic/open-item/open-items.service";
import AccountStatementsAttributes from "@albatrosdeveloper/ave-models-package/lib/schemas/account-statements/account-statements.entity";
import { OpenItemExport } from "./tasks/exports/open-item/open-items-export";
import { OrderPaymentAService } from "app/modules/components/reports/historic/order-payment/order-payment.service";
import OrderPaymentAttributes from "@albatrosdeveloper/ave-models-package/lib/schemas/process/order-payment/order-payment.entity";
import { OrderPaymentExport } from "./tasks/order-payment-export";
import { SalesQuotationListExport } from "./tasks/sales-quotation-list-export";
import { ExportTaskCreationOptions } from "./tasks/core/export-task.interfaces";
import { ExportTask } from "./tasks/core/export-task";
import { SalesDistributionService } from "app/modules/components/assistant/sales-distribution/sales-distribution.service";
import { SeleDstributionExport } from "./tasks/sale-distribution-export";
import { IFilterMaterial } from "app/modules/components/assistant/sales-distribution/interfaces/filter_material.interface";
import { IOptionFilterDistribution } from "app/modules/components/assistant/sales-distribution/interfaces/option_filter.interface";

@Injectable({ providedIn: "root" })
export class ExportService {
  public readonly active$: Observable<boolean>;
  public readonly visible$: Observable<boolean>;
  public readonly busy$: Observable<boolean>;
  public readonly change$: Observable<void>;

  constructor(
    private store: ExportStoreService,
    private orderCustomerService: OrderCustomerService,
    private ordersListService: OrdersListService,
    private openItemsService: OpentItemsService,
    private orderpaymentService: OrderPaymentAService,
    private saleDistributionService: SalesDistributionService
  ) {
    this.active$ = store.active$;
    this.visible$ = store.visible$.asObservable();
    this.busy$ = store.busy$.asObservable();
    this.change$ = store.change$.asObservable();
  }

  private discardCurrentTaskToStartAnother(id: string) {
    const task = this.getTask(id);
    if (!task) return true;
    if (task.status === ExportTaskStatus.Busy) return false;
    this.store.removeTask(id);
    return true;
  }

  task(options: ExportTaskCreationOptions) {
    if (!this.discardCurrentTaskToStartAnother(options.id)) return null;
    return new ExportTask(this.store, options);
  }

  /////////////////////////
  // Factories

  createForOrderCustomer(
    itemCount: number,
    filter: CombinedFilter<OrderDetailAttributes>,
    range: ExportRange,
    format: ExportFileFormat,
    exportId: string,
    title: string
  ) {
    if (!this.discardCurrentTaskToStartAnother(exportId)) return null;
    return new OrderCustomerExport(
      itemCount,
      filter,
      range,
      format,
      this.store,
      this.orderCustomerService,
      exportId,
      title
    );
  }

  createForOpenItemList(
    itemCount: number,
    filter: CombinedFilter<AccountStatementsAttributes>,
    range: ExportRange,
    format: ExportFileFormat,
    exportId: string,
    title: string
  ) {
    if (!this.discardCurrentTaskToStartAnother(exportId)) return null;
    return new OpenItemExport(
      itemCount,
      filter,
      range,
      format,
      this.store,
      this.openItemsService,
      exportId,
      title
    );
  }

  createForOrderPaymentList(
    itemCount: number,
    filter: CombinedFilter<OrderPaymentAttributes>,
    range: ExportRange,
    format: ExportFileFormat
  ) {
    if (!this.discardCurrentTaskToStartAnother("order-payment")) return null;
    return new OrderPaymentExport(
      itemCount,
      filter,
      range,
      format,
      this.store,
      this.orderpaymentService
    );
  }

  createForSalesQuotationList(
    itemCount: number,
    filter: CombinedFilter<OrderAttributes>,
    filterData: any,
    range: ExportRange,
    format: ExportFileFormat
  ) {
    if (!this.discardCurrentTaskToStartAnother("sales-quotation-list"))
      return null;
    return new SalesQuotationListExport(
      itemCount,
      filter,
      filterData,
      range,
      format,
      this.store,
      this.ordersListService
    );
  }

  // createForOrderPayment(
  //   itemCount: number,
  //   filter: CombinedFilter<OrderPaymentAttributes>,
  //   // filterData: any,
  //   range: ExportRange,
  //   format: ExportFileFormat,
  //   decimalPipe: DecimalPipe,
  // ) {
  //   if (!this.discardCurrentTaskToStartAnother("order-payment-c")) return null;
  //   return new OrderPaymentExportC(
  //     itemCount,
  //     filter,
  //     // filterData,
  //     range,
  //     format,
  //     this.store,
  //     this.orderPaymentService,
  //     decimalPipe,
  //   );
  // }

  createForSalesDistribution(
    itemCount: number,
    filter: CombinedFilter<any>,
    filterData: IOptionFilterDistribution,
    range: ExportRange,
    format: ExportFileFormat,
    option: string
  ) {
    if (!this.discardCurrentTaskToStartAnother("sales-distribution"))
      return null;
    return new SeleDstributionExport(
      itemCount,
      filter,
      filterData,
      range,
      format,
      this.store,
      this.saleDistributionService,
      option
    );
  }

  // Factories
  /////////////////////////

  taskIsActive(id: string) {
    // A task is active if it is listed in the tasks array of the store.
    return this.store.tasks.some(
      (someTask) =>
        someTask.id === id && someTask.status === ExportTaskStatus.Busy
    );
  }

  getTask(id: string) {
    return this.store.tasks.find((fromTask) => fromTask.id === id);
  }

  toggleVisibility() {
    this.store.setVisible(!this.store.visible$.value);
  }

  setVisible(visible: boolean) {
    this.store.setVisible(visible);
  }

  getTasks() {
    return this.store.tasks;
  }
}
